/**
 * SEOHead — declarative SEO for a page.
 *
 * Pushes SEO payload into SEOContext via setPageSEO. A single central applier
 * (in App.js: <SEOApplier />) reads the current context state and applies to
 * the DOM. This avoids parent/child useEffect ordering conflicts that cause
 * parent SEOHead to overwrite child SEO.
 *
 * Usage:
 *   <SEOHead />                                   // pakai default global SEO
 *   <SEOHead title="Katalog" description="..." /> // override
 *   <SEOHead seo={productSEO} jsonLd={...} />     // full override
 */
import { useEffect } from "react";
import { useSEO } from "@/context/SEOContext";

export default function SEOHead({
  title,
  description,
  keywords,
  image,
  canonical,
  robots,          // opsional: "noindex,follow" dsb. (route policy tetap prioritas)
  ogType,
  jsonLd,
  seo: seoOverride,
}) {
  const { setPageSEO } = useSEO();
  const jsonLdKey = JSON.stringify(jsonLd);

  useEffect(() => {
    setPageSEO({
      title: title || null,
      description: description || null,
      keywords: keywords || null,
      image: image || null,
      canonical: canonical || null,
      robots: robots || null,
      ogType: ogType || null,
      seo: seoOverride || null,
      jsonLd: jsonLd || null,
    });
    return () => setPageSEO(null);
    // jsonLdKey lets us dedupe when jsonLd payload changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords, image, canonical, robots, ogType, seoOverride, jsonLdKey, setPageSEO]);

  return null;
}
